import * as Sentry from '@sentry/browser';
import _ from 'lodash';

import config from '../config';
import InAppStore from '../stores/InAppStore';
import UserStore from '../stores/UserStore';
import OrderStore from '../stores/OrderStore';
import CurrentTripStore from '../stores/CurrentTripStore';
import Order from '../records/Order';
import baseCsp from '../config/csp/baseCsp';

// Parses a CSP script-src entry (e.g. "https://*.example.com") into its host,
// so eval violations can be matched against hosts our own CSP already trusts
// to execute script — reusing this list means it never drifts out of sync
// with the policy actually served.
function parseCspSourceHost (entry) {
    var match = /^https?:\/\/(\*\.)?([^/]+)$/.exec(entry);
    if (!match) {
        return null;
    }
    return { host: match[2], wildcard: Boolean(match[1]) };
}

// Matches a blocked resource against the suppression list. Each entry pins the
// hostname on a label boundary (so 'evil-ecs.us-east-1.on.aws.attacker.com' and a
// path segment like '/ecs.us-east-1.on.aws/' cannot pose as the real host) and may
// pin the path and directive as well. A blockedURI that is not a URL — 'inline',
// 'eval', 'self' — never matches.
function isIgnoredBlockedResource (blocked, directive, ignoredBlockedResources) {
    var url;
    try {
        url = new URL(blocked);
    } catch (err) {
        return false;
    }
    return ignoredBlockedResources.some(function (entry) {
        var hostMatches = entry.subdomains
            ? url.hostname === entry.host || url.hostname.endsWith('.' + entry.host)
            : url.hostname === entry.host;
        if (!hostMatches) {
            return false;
        }
        if (entry.path && url.pathname !== entry.path) {
            return false;
        }
        if (entry.directive && directive !== entry.directive) {
            return false;
        }
        return true;
    });
}

// Vendor SDKs whose CSP violations are known, accepted and not ours to fix. Their
// scripts inject inline <script> and call eval, both of which our nonce-based CSP
// always blocks (CORE-19821) — a nonce makes the browser ignore 'unsafe-inline',
// so allowlisting the host lets the SDK load but never lets its injected code run.
// The block is the policy working as intended, so every directive from these hosts
// is silenced rather than reported.
function isIgnoredSourceHost (sourceFile, ignoredSourceHosts) {
    if (!sourceFile) {
        return false;
    }
    var sourceHost;
    try {
        sourceHost = new URL(sourceFile).hostname;
    } catch (err) {
        return false;
    }
    return ignoredSourceHosts.some(function (host) {
        return sourceHost === host || sourceHost.endsWith('.' + host);
    });
}

function isTrustedEvalSource (sourceFile, trustedScriptSrcHosts) {
    if (!sourceFile) {
        return false;
    }
    var sourceHost;
    try {
        sourceHost = new URL(sourceFile).hostname;
    } catch (err) {
        return false;
    }
    return trustedScriptSrcHosts.some(function (entry) {
        var parsed = parseCspSourceHost(entry);
        if (!parsed) {
            return false;
        }
        return parsed.wildcard
            ? sourceHost === parsed.host || sourceHost.endsWith('.' + parsed.host)
            : sourceHost === parsed.host;
    });
}

// Evaluated per event, not at module load — config.init() runs at app boot,
// after this module is imported, so a module-scope check would always read
// the default environment and leak name/email to Sentry in production.
function getUserFields () {
    return config.get('environment') === 'prod' ? ['id'] : ['id', 'fullName', 'email'];
}

function summariseProducts (order) {
    return order.getProducts().map((fare) => ({
        id: fare.id,
        category: fare.category,
        ticketType: _.get(fare, 'ticketType.name'),
        origin: _.get(fare, 'origin.name'),
        destination: _.get(fare, 'destination.name'),
        adults: fare.adults(),
        children: fare.children(),
        price: fare.getTotalPrice(),
        railcards: fare.getRailcards().map((railcard) => railcard.code).toJS(),
    })).toJS();
}

function summariseStation (station) {
    return { crs: _.get(station, 'crs'), nlc: _.get(station, 'nlc') };
}

// The trip carries the season ticket photocard (name, date of birth, address,
// number) and is persisted with no expiry, so it would ride along on every
// later error — only station codes, fare class and passenger counts go out.
function summariseTrip (trip) {
    const search = trip.search;
    const destination = _.get(search, 'destination', { nlc: _.get(search, 'destinationNlc') });
    return {
        origin: summariseStation(_.get(search, 'origin')),
        destination: summariseStation(destination),
        via: summariseStation(_.get(search, 'via')),
        fareClass: trip.fareClass,
        adults: _.get(search, 'adults'),
        children: _.get(search, 'children'),
    };
}

// Serialising the order and trip records wholesale would send passenger names,
// photocard details and smartcard numbers to Sentry, so only an explicit
// allow-list is reported. This runs inside beforeSend, where a throw would
// suppress the very error being reported, so it can never be allowed to fail.
function serialise (summarise, record) {
    try {
        return JSON.stringify(summarise(record));
    } catch (error) {
        return '<unavailable>';
    }
}

const excludeErrors = [
    /TypeError: .*(null.*_currentElement|_currentElement.*null)/,
    /Error: Minified exception occurred/,
    /TypeError: Network request failed/,
    /TypeError: Failed to fetch/,
    /TypeError: Load failed/,
    /NetworkError when attempting to fetch resource/,
    /TypeError: this\.get\(\.\.\.\)\.querySelectorAll is not a function/,
    /TypeError: plugin\.setSuspendState is not a function/,
    /SyntaxError: Unexpected token </,
    /TypeError: Failed to execute 'removeChild' on 'Node': parameter 1 is not of type 'Node'/,
    /Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node/,
    /undefined: \[object Event\]/,
    /Error: Out of memory/,
    /undefined: RangeError: Maximum call stack size exceeded/
];

function isPayPalSdkFrame (frame) {
    try {
        const url = new URL(frame.abs_path);
        return url.hostname === 'www.paypal.com' && url.pathname === '/sdk/js';
    } catch (error) {
        return false;
    }
}

// The PayPal SDK's Pay Later message looks up its container on a timer after
// Messages.driver.destroyAll(), by which point we have unmounted it, so it
// throws on null.nodeType (BOOKING-ENGINE-PROD-3TK7). GlobalErrorBoundary
// already hides it from the customer; this stops it reaching Sentry. Only
// errors raised entirely inside the SDK are dropped, so a PayPal failure that
// passes through our code is still reported.
function isPayPalMessagesTeardownError (exception) {
    const frames = exception?.stacktrace?.frames;
    return exception.type === 'TypeError' &&
        /nodeType/.test(exception.value) &&
        Boolean(frames && frames.length) &&
        frames.every(isPayPalSdkFrame);
}

function addErrorContext (event, hint) {
    const currentUser = UserStore.getCurrent();
    if (currentUser.isLoggedIn()) {
        event.user = _.pick(currentUser, getUserFields());
    }
    event.tags = Object.assign({}, event.tags, {
        'app': config.get('name'),
        'in-app': !!InAppStore.isInApp()
    });

    // The store can hold an explicit null (get()'s default only covers a missing key) —
    // throwing here would replace the error being reported with our own TypeError.
    const order = OrderStore.getCurrent() ?? new Order();
    const trip = CurrentTripStore.get();
    const originalException = (hint && hint.originalException) || {};
    event.extra = Object.assign({}, event.extra, originalException.extra, {
        'order.id': order.id,
        'order.state': order.state,
        'order.products': serialise(summariseProducts, order),
        'order.totalPrice': order.getTotalPrice(),
        'order.expiry': order.expiry && order.expiry.format(),
        'order.fulfilment': order.getDeliveryMethodName(),
        'trip.type': trip.getType(),
        'trip.isValid': trip.isValid(),
        'trip': serialise(summariseTrip, trip)
    });

    return event;
}

function shouldSendError (event) {
    const exception = event.exception && event.exception.values && event.exception.values[0];
    const message = exception
        ? (exception.type ? exception.type + ': ' : '') + exception.value
        : event.message;
    if (!message || excludeErrors.some((pattern) => message.match(pattern)) ||
        (exception && isPayPalMessagesTeardownError(exception))) {
        return false;
    }
    return true;
}

function beforeSend (event, hint) {
    if (!shouldSendError(event)) {
        return null;
    }
    return addErrorContext(event, hint);
}

export function init (version) {
    const dsn = InAppStore.isInApp() ? config.get('ravenDSNTAB') : config.get('ravenDSN');
    if (dsn) {
        Sentry.init({
            dsn,
            release: version,
            initialScope: {
                tags: {
                    brand: config.get('brand'),
                    environment: config.get('environment')
                }
            },
            beforeSend
        });

        // CSP → Sentry bridge using captureException
        if (typeof document !== 'undefined' && document.addEventListener) {
            var reportedCspViolations = {};
            // Third-party hosts blocked by CSP that we cannot/will not whitelist.
            // Suppress these to avoid Sentry quota burn — they are not bugs in our code.
            var ignoredBlockedResources = [
                // FullStory event collection (loaded via GTM) — per-org subdomain
                { host: 'ecs.us-west-2.on.aws', subdomains: true },
                // LemonPI ad-retargeting scraper (loaded via GTM)
                { host: 'd.lemonpi.io' },
                // X (Twitter) ads conversion pixel (loaded via GTM) — pinned to the one
                // observed endpoint (BOOKING-ENGINE-PROD-3W9A)
                { host: 'analytics.twitter.com', path: '/i/adsctp', directive: 'connect-src' },
                // Meta Conversions API Gateway (CORE-23382). Both backends sit on
                // hostnames that rotate — a per-task ECS hostname and a per-revision
                // Cloud Run URL — so the path and directive are pinned too, keeping
                // the suppression to this one endpoint rather than every service on
                // those clouds.
                { host: 'ecs.us-east-1.on.aws', subdomains: true, path: '/events', directive: 'connect-src' },
                { host: 'us-central1.run.app', subdomains: true, path: '/events', directive: 'connect-src' },
            ];
            // Vendor script hosts silenced entirely, whatever the directive — see
            // isIgnoredSourceHost.
            var ignoredSourceHosts = [
                'sojern.com', // Sojern tracking SDK, loaded via GTM
                'dwin1.com', // Awin affiliate MasterTag, loaded via GTM
            ];
            // Hosts already trusted to execute script on this page (per our own CSP) —
            // an eval() call from one of these isn't a new risk, just noise from a
            // vendor SDK (Sitecore Engage, Sojern, etc). We will never whitelist eval
            // itself, so an eval violation from anywhere else is treated as a possible
            // injection attempt and always reported.
            var trustedScriptSrcHosts = baseCsp.buildBaseCspForEnv(config.get('environment')).cspScriptSrc;
            document.addEventListener('securitypolicyviolation', function (e) {
                try {
                    // Browser extensions inject scripts that can trigger eval violations
                    // against the page CSP — these are not our violations, ignore them.
                    var sourceFile = e.sourceFile || '';
                    if (
                        sourceFile.startsWith('chrome-extension://') ||
                        sourceFile.startsWith('moz-extension://') ||
                        sourceFile.startsWith('safari-extension://') ||
                        sourceFile.startsWith('safari-web-extension://')
                    ) {
                        return;
                    }

                    if (isIgnoredSourceHost(sourceFile, ignoredSourceHosts)) {
                        return;
                    }

                    var directive = e.effectiveDirective || e.violatedDirective || 'csp';
                    var blocked = e.blockedURI || '<inline>';

                    if (isIgnoredBlockedResource(blocked, directive, ignoredBlockedResources)) {
                        return;
                    }

                    var isEval = blocked === 'eval';
                    if (isEval && isTrustedEvalSource(sourceFile, trustedScriptSrcHosts)) {
                        return;
                    }

                    // Include sourceFile in the dedup key — otherwise an expected eval hit
                    // from a known vendor (reported first, since those load early) would
                    // permanently suppress a later eval from an unrecognised/malicious
                    // source in the same session, since both share the same directive
                    // and blockedURI ('eval').
                    var message = directive + ": blocked '" + blocked + "'" + (sourceFile ? ' from ' + sourceFile : '');

                    // A single blocked-but-retrying tag can emit hundreds of identical
                    // violations per session — report each directive/URI/source once.
                    if (reportedCspViolations[message]) {
                        return;
                    }
                    reportedCspViolations[message] = true;

                    var error = new Error(message);
                    error.name = 'CSPViolationError';

                    error.extra = {
                        isCspViolation: true,
                        directive: directive,
                        violatedDirective: e.violatedDirective,
                        blockedURI: e.blockedURI,
                        documentURI: e.documentURI,
                        originalPolicy: e.originalPolicy,
                        referrer: e.referrer,
                        sourceFile: e.sourceFile,
                        lineNumber: e.lineNumber,
                        columnNumber: e.columnNumber,
                        disposition: e.disposition
                    };

                    // eval from a source outside our own CSP script-src allowlist is not
                    // expected vendor noise — flag it so it can be alerted on separately.
                    if (isEval) {
                        error.extra.securityAlert = true;
                    }

                    Sentry.captureException(error, { extra: error.extra });
                } catch (err) {
                    console.warn('Error capturing CSP violation:', err);
                }
            });
        }
    }
}

export default function reportError (error) {
    Sentry.captureException(error, { extra: error.extra });
}
